import {useEffect, useState, type FormEvent} from "react";
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import {useTranslation} from "react-i18next";
import {playerToString} from "../../util/StringUtils";
import {InputGroup} from "react-bootstrap";
import PlayerNameWithFlag from "../players/PlayerNameWithFlag";
import {MATCH_RESULTS, type MatchResult} from "../../util/Constants";
import {isDefaultWin} from "../../util/HelperUtils";
import {getLoggedUser, isAdmin} from "../../util/SecurityUtils";
import {useConfetti} from "../../hooks/useConfetti";
import {useSetMatchResult} from "../../hooks/mutations/useMatchMutations";
import type {Match} from "../../types/domain";

interface SetMatchResultModalProps {
    isOpen: boolean;
    closeModal: () => void;
    match: Match;
    /** Tournament-computed manage permission; decides which result endpoint is used. */
    canManage?: boolean;
}

interface SetResultRow {
    resultOrder: number;
    playerOneScore: number | "";
    playerTwoScore: number | "";
}

const isMatchResult = (value: string): value is MatchResult =>
    value === MATCH_RESULTS.PLAYER_ONE_WON ||
    value === MATCH_RESULTS.PLAYER_TWO_WON ||
    value === MATCH_RESULTS.PLAYER_ONE_DEFAULT ||
    value === MATCH_RESULTS.PLAYER_TWO_DEFAULT ||
    value === MATCH_RESULTS.IN_PROGRESS ||
    value === MATCH_RESULTS.CANCELED;

function SetMatchResultModal(props: SetMatchResultModalProps) {
    const { t } = useTranslation();
    const [ state, setState ] = useState<MatchResult>(MATCH_RESULTS.PLAYER_ONE_WON)
    const [ results, setResults ] = useState<SetResultRow[]>([])
    const [ submitButtonDisabled, setSubmitButtonDisabled ] = useState(true)
    const [ validated, setValidated ] = useState(false);
    const { fireWinConfetti } = useConfetti();
    const setMatchResultMutation = useSetMatchResult();

    useEffect(() => {
        if (props.isOpen === false) {
            setValidated(false)
            setSubmitButtonDisabled(false)
        }
    }, [props.isOpen]);

    useEffect(() => {
        if (props.match !== null) {
            if (props.match.state !== MATCH_RESULTS.IN_PROGRESS) {
                setState(props.match.state)
            }
            const fullResults: SetResultRow[] = []
            for (let i = 0; i < props.match.results.length; i++) {
                const result = props.match.results[i]
                fullResults.push({resultOrder: i + 1, playerOneScore: result.playerOneScore, playerTwoScore: result.playerTwoScore})
            }
            for (let i = fullResults.length; i < (props.match.setsNeededToWin * 2 - 1); i++) {
                fullResults.push({resultOrder: i + 1, playerOneScore: "", playerTwoScore: ""})
            }
            setResults(fullResults)
            setSubmitButtonDisabled(false)
        }
    }, [props.match]);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>, consideredSets: number) => {
        const form = event.currentTarget;
        event.preventDefault();
        event.stopPropagation();
        if (form.checkValidity() === false) {
            setValidated(true);
        } else {
            setSubmitButtonDisabled(true)
            const resultDto = {
                state: state,
                results: isDefaultWin(state) ? [] : results.slice(0, consideredSets).map((result, index) => {
                    return {
                        resultOrder: index + 1,
                        playerOneScore: Number(result.playerOneScore),
                        playerTwoScore: Number(result.playerTwoScore)
                    }
                })
            }
            await setMatchResultMutation.mutateAsync({
                matchId: props.match.id,
                resultDto,
                asOrganizer: props.canManage ?? isAdmin(),
            });

            const loggedUserId = getLoggedUser()?.playerId;
            const playerOneWon = state === MATCH_RESULTS.PLAYER_ONE_WON || state === MATCH_RESULTS.PLAYER_ONE_DEFAULT;
            const playerTwoWon = state === MATCH_RESULTS.PLAYER_TWO_WON || state === MATCH_RESULTS.PLAYER_TWO_DEFAULT;
            const userIsPlayerOne = props.match.playerOne.id === loggedUserId;
            const userIsPlayerTwo = props.match.playerTwo.id === loggedUserId;

            if ((userIsPlayerOne && playerOneWon) || (userIsPlayerTwo && playerTwoWon)) {
                fireWinConfetti();
            }

            props.closeModal()
        }
    };

    const setPlayerScore = (index: number, score: string, isPlayerOne: boolean) => {
        const newResults = [...results]
        const parsed = score === "" ? "" : parseInt(score, 10);
        if (isPlayerOne) {
            newResults[index].playerOneScore = parsed as number | ""
        } else {
            newResults[index].playerTwoScore = parsed as number | ""
        }
        setResults(newResults)
    }

    const isResultValid = (result: SetResultRow): boolean => {
        if (result.playerOneScore !== "" && result.playerTwoScore !== "") {
            const p1 = result.playerOneScore;
            const p2 = result.playerTwoScore;
            return ((p1 > p2) && ((p1 === 11 && p2 < 10) || (p1 > 11 && (p1 - p2) === 2)))
                || ((p1 < p2) && ((p2 === 11 && p1 < 10) || (p2 > 11 && (p2 - p1) === 2)))
        }
        return true
    }

    const getResultStyle = (isValid: boolean, score: number | "", otherScore: number | "") => {
        if (isValid && score !== "" && otherScore !== "") {
            if (score > otherScore) {
                return "win"
            } else {
                return "loss"
            }
        }
        return ""
    }

    let enabledSets = 1
    let playerOneSetsWon = 0
    let playerTwoSetsWon = 0
    for (let i = 0; i < results.length; i++) {
        if (playerOneSetsWon === props.match.setsNeededToWin || playerTwoSetsWon === props.match.setsNeededToWin) {
            enabledSets = playerOneSetsWon + playerTwoSetsWon
            break
        }
        const result = results[i]
        if (result.playerOneScore !== "" && result.playerTwoScore !== "") {
            const p1 = result.playerOneScore;
            const p2 = result.playerTwoScore;
            if ((p1 > p2) && (p1 === 11 || ((p1 - p2) === 2))) {
                playerOneSetsWon += 1
            } else if ((p1 < p2) && (p2 === 11 || ((p2 - p1) === 2))) {
                playerTwoSetsWon += 1
            } else {
                break
            }
        } else {
            break
        }
        enabledSets += 1
    }
    enabledSets = Math.max(props.match.setsNeededToWin, enabledSets)

    const enoughSetsWon = (state === MATCH_RESULTS.PLAYER_ONE_WON && playerOneSetsWon === props.match.setsNeededToWin) ||
      (state === MATCH_RESULTS.PLAYER_TWO_WON && playerTwoSetsWon === props.match.setsNeededToWin)
    const canSubmit = isDefaultWin(state) || enoughSetsWon

    const isMatchResultInvalid = (state === MATCH_RESULTS.PLAYER_ONE_WON && playerOneSetsWon < playerTwoSetsWon)
        || (state === MATCH_RESULTS.PLAYER_TWO_WON && playerOneSetsWon > playerTwoSetsWon)

    const match = props.match

    return (
      <Modal
        show={props.isOpen}
        onHide={props.closeModal}
      >
          <Modal.Header closeButton style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
              <Modal.Title>{t("setResult")}</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}>
              <Form noValidate validated={validated} onSubmit={(event) => handleSubmit(event, enabledSets)} className="hide-arrows">
                  <Form.Group className="mb-3">
                      <Form.Label>{t("matchResult")}: </Form.Label>
                      <Form.Select onChange={e => { const v = e.target.value; if (isMatchResult(v)) setState(v); }} value={state} isInvalid={isMatchResultInvalid}
                                   style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--text-primary)' }}>
                          <option value={MATCH_RESULTS.PLAYER_ONE_WON}>{t("playerWon", {player: playerToString(match.playerOne, true)})}</option>
                          <option value={MATCH_RESULTS.PLAYER_TWO_WON}>{t("playerWon", {player: playerToString(match.playerTwo, true)})}</option>
                          <option value="-" disabled>-------------------------</option>
                          <option value={MATCH_RESULTS.PLAYER_ONE_DEFAULT}>{t("playerDefaultWin", {player: playerToString(match.playerOne, true)})}</option>
                          <option value={MATCH_RESULTS.PLAYER_TWO_DEFAULT}>{t("playerDefaultWin", {player: playerToString(match.playerTwo, true)})}</option>
                      </Form.Select>
                  </Form.Group>
                  {isDefaultWin(state) ? null :
                      <div className="versus-row">
                          <PlayerNameWithFlag player={props.match.playerOne} includeElo={false} />
                          <PlayerNameWithFlag player={props.match.playerTwo} includeElo={false} />
                      </div>
                  }
                  {isDefaultWin(state) ? null : results.map((result, index) => {
                      const isValid = isResultValid(result)
                      const isDisabled = index >= enabledSets
                      return (
                        <InputGroup className="mb-2" size="sm" key={"set-input-" + index}>
                            <InputGroup.Text style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}>{t("setNumber", {number: index + 1})}</InputGroup.Text>
                            <Form.Control type="number" min="0" onChange={e => setPlayerScore(index, e.target.value, true)}
                                          value={isDisabled ? "" : results[index].playerOneScore} disabled={isDisabled} isInvalid={!isValid}
                                          className={getResultStyle(isValid, results[index].playerOneScore, results[index].playerTwoScore)}
                                          style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--text-primary)' }} />
                            <InputGroup.Text style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}> : </InputGroup.Text>
                            <Form.Control type="number" min="0" onChange={e => setPlayerScore(index, e.target.value, false)}
                                          value={isDisabled ? "" : results[index].playerTwoScore} disabled={isDisabled} isInvalid={!isValid}
                                          className={getResultStyle(isValid, results[index].playerTwoScore, results[index].playerOneScore)}
                                          style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--text-primary)' }} />
                        </InputGroup>
                      )
                  })}
                  <Button type="submit" disabled={submitButtonDisabled || !canSubmit} className="mt-3" style={{marginLeft: "auto", width: "100%"}}>{t("save")}</Button>
              </Form>
          </Modal.Body>
      </Modal>
    );
}

export default SetMatchResultModal
